import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import Field, { fieldTypes } from "components/Field";
import FileImport from "components/FileImport";
import { InfoLabel } from "components/InfoLabel";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectIsGovMode } from "slices/app/selectors";
import { importUsersFromCsvCloudAdmin } from "slices/systemAdmin/actions/systemAdmin";
import { importUsersFromCsv } from "slices/systemAdmin/actions/users";
import { selectIsUsernameAndPassword } from "slices/systemAdmin/selectors";
import { importUsersWithRolesFromCsv } from "slices/userAdmin/actions/userAdmin";

const ImportUsersModal = ({ workspaceId }) => {
  const dispatch = useDispatch();

  const [selectedFile, setSelectedFile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const isUserNamePassword = useSelector(selectIsUsernameAndPassword);
  const isGovMode = useSelector(selectIsGovMode);
  const showPasswordField = !isGovMode && isUserNamePassword;

  const isSsoDisabled = () => selectedFile !== "";
  const isUserPassDisabled = () =>
    selectedFile !== "" &&
    password !== "" &&
    confirmPassword !== "" &&
    passwordError === "" &&
    confirmPasswordError === "";

  const isOkButtonEnabled = () =>
    showPasswordField ? isUserPassDisabled() : isSsoDisabled();

  const handleSubmit = () => {
    if (isOkButtonEnabled()) {
      const formData = new FormData();
      formData.append("usersFile", selectedFile, selectedFile.name);
      formData.append("password", password);
      if (workspaceId) {
        formData.append("workspaceId", workspaceId);
      }

      if (window.location.hash.includes("/system-admin")) {
        dispatch(importUsersFromCsv(formData));
      } else if (window.location.hash.includes("/cloud-admin")) {
        dispatch(importUsersFromCsvCloudAdmin(formData));
      } else {
        dispatch(importUsersWithRolesFromCsv(formData));
      }
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const onEnterKeyDown = ({ key }) => {
    if (key === "Enter" && isOkButtonEnabled()) {
      handleSubmit();
    }
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.IMPORT_USERS));
  };

  const formatExampleBody = () => (
    <div className="import__users__modal__format">
      <span>Format Example</span>
      <blockquote>
        {window.location.hash.includes("/system-admin") ? (
          <small>
            <strong>CSV Columns: </strong>email, name, surname <br />
            john.doe@example, John, Doe <br />
            jane.doe@example, Jane, Doe
          </small>
        ) : (
          <small>
            <strong>CSV Columns: </strong>email, name, surname, licensed,
            dictionaryAdmin, readabilitySettings, acronymSettings,
            discoverySettings, excelOutputs, proposalStagesAdmin,
            docStyleTemplatesAdmin, currenciesAdmin, archiveStatusesAdmin,
            usersAdmin:
            <br />
            john.doe@example, John, Doe, true, true, true, true, true, true,
            true, true, false, false, false <br />
            jane.doe@example, Jane, Doe, false, true, false, false, false,
            false, false, false, false, true, false
          </small>
        )}
      </blockquote>
    </div>
  );

  const passwordBody = () => (
    <div className="import__users__modal__password">
      <span>
        <InfoLabel
          text={<strong>One Time Password</strong>}
          tooltipText="Every user will have their password reset to this One Time password. The first time a user logs in using this One Time password they will be forced to provide a new password to secure their future logins. The password must be at least 8 characters long and contain at least 1 letter and 1 number."
        />
      </span>
      <Field
        type={fieldTypes.passwordAndConfirm}
        propsObject={{
          password,
          setPassword,
          passwordError,
          setPasswordError,
          confirmPassword,
          setConfirmPassword,
          confirmPasswordError,
          setConfirmPasswordError,
        }}
      />
    </div>
  );

  return (
    <Modal
      data-test="newUserModal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      backdrop
      centered
      onKeyDown={onEnterKeyDown}
    >
      <Modal.Header>
        <b className="import__users__modal__header">Import Users From CSV</b>
      </Modal.Header>
      <Modal.Body>
        <div className="import__users__modal">
          <FileImport
            accepts=".csv"
            handleFileChange={handleFileChange}
            selectedFile={selectedFile}
          />

          {formatExampleBody()}
          {showPasswordField && passwordBody()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="saveButton"
          variant={NewButtonVariant.l1}
          onClick={handleSubmit}
          disabled={!isOkButtonEnabled()}
        >
          Add User(s)
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportUsersModal;

ImportUsersModal.propTypes = {
  workspaceId: PropTypes.number,
};
